// Grids & Gantts — "Due In", the way the structure this page
// replaces counts it down.
//
// The column answers what a due date alone does not: how long is left. The
// structure prints it as a running duration — "13w 5d 23h 48m" — counted to
// the END of the due day, which is why every row ends in 23h-something for
// most of the working day and why two screenshots taken minutes apart differ
// in the minutes and nothing else.
//
// BLANK IS A REAL ANSWER, twice over:
//   - no due date → there is nothing to count down to;
//   - the work is Done → the countdown stopped being a fact about it. A
//     finished item three weeks past its old due date is not "-21 days"; it
//     is done.
//
// PAST THE DATE IS ONE WORD, not a negative duration: "Overdue". The
// structure says it that way, and a reader scanning a column of durations
// finds the word faster than a minus sign.
//
// The arithmetic is on LOCAL calendar days. A due date carries no time, so
// reading it as UTC midnight would make it land a day early or late
// depending on where the reader is sitting.

const DATE_ONLY_RE = /^(\d{4})-(\d{2})-(\d{2})/;
const MIN = 60 * 1000;
const HOUR = 60 * MIN;
const DAY = 24 * HOUR;
const WEEK = 7 * DAY;

// Local midnight of the calendar day a date value names. Accepts the bare
// "YYYY-MM-DD" the field holds and the full timestamp an EAV row can carry
// ("2026-10-02T00:00:00.000+0200") — the leading date is read off the TEXT
// in both cases, so the stored calendar day is the one used.
export function dateMidnight(value) {
  if (value == null) return null;
  const text = String(value).trim();
  if (!text) return null;
  const m = DATE_ONLY_RE.exec(text);
  if (m) return new Date(+m[1], +m[2] - 1, +m[3]);
  const d = new Date(text);
  if (Number.isNaN(d.getTime())) return null;
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

// Milliseconds from now to the END of the due day. Null when unreadable.
export function msLeft(value, now = new Date()) {
  const due = dateMidnight(value);
  if (!due) return null;
  return (due.getTime() + DAY) - now.getTime();
}

// "13w 5d 23h 48m". A unit that is zero is left out, but the ones after it
// are not: "66w 23h 53m" is a real answer and reads correctly.
export function formatLeft(ms, { units = 4 } = {}) {
  if (ms == null) return '';
  let rest = Math.max(0, ms);
  const w = Math.floor(rest / WEEK); rest -= w * WEEK;
  const d = Math.floor(rest / DAY); rest -= d * DAY;
  const h = Math.floor(rest / HOUR); rest -= h * HOUR;
  const m = Math.floor(rest / MIN);
  const parts = [];
  if (w) parts.push(`${w}w`);
  if (d) parts.push(`${d}d`);
  if (h) parts.push(`${h}h`);
  if (m || !parts.length) parts.push(`${m}m`);
  return parts.slice(0, Math.max(1, units)).join(' ');
}

export const isDoneItem = (it) => {
  const cat = it && it.fields && it.fields.status && it.fields.status.statusCategory;
  if (!cat) return false;
  const key = String(cat.key || '').toLowerCase();
  if (key) return key === 'done';
  return String(cat.name || '').toLowerCase() === 'done';
};

// The whole cell's answer: null (draw nothing), or what to draw.
//   { overdue: true }                → "Overdue"
//   { overdue: false, label, ms }    → the running duration
export function dueInFor(item, value, now = new Date()) {
  if (item && isDoneItem(item)) return null;
  const ms = msLeft(value, now);
  if (ms == null) return null;
  if (ms <= 0) return { overdue: true, label: 'Overdue', ms };
  return { overdue: false, label: formatLeft(ms), ms };
}

// ---- dates, printed one way, in one place ------------------------------------
//
// "11/09/2026" — day, month, four-digit year, which is how this client writes
// a date everywhere else.
//
// IT WAS "11/Sep/26", copied from the Jira structure this page replaces, and
// that is two compromises in one string. The month abbreviation is English,
// so it stops being readable the moment anybody's browser is not; and a
// two-digit year is the shortcut that has to be undone by hand every time a
// date is compared with a document, a contract or an invoice.
//
// ONE FUNCTION DECIDES, which is why this is a four-line change rather than a
// hunt: the grid, the span a folder shows and both exports all print a date
// through here.
export function formatDate(value) {
  const d = dateMidnight(value);
  if (!d) return '';
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  return `${dd}/${mm}/${d.getFullYear()}`;
}

// The ISO the date input wants back.
export function isoOf(value) {
  const d = dateMidnight(value);
  if (!d) return '';
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
